import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  type Feature,
  featureFromPath,
  trackFeatureTimeSpent,
  trackFeatureViewed,
} from "../utils/analytics";
import { applyRouteReplayBoosts } from "../utils/replayBoosts";

const MIN_DWELL_MS = 3 * 1000;
const MAX_DWELL_MS = 30 * 60 * 1000;

interface DwellState {
  feature: Feature;
  since: number | null;
}

export default function useFeatureAnalytics(): void {
  const location = useLocation();
  const dwell = useRef<DwellState | null>(null);

  const flushDwell = () => {
    const state = dwell.current;
    if (!state || state.since === null) {
      return;
    }
    const durationMs = Math.min(Date.now() - state.since, MAX_DWELL_MS);
    if (durationMs >= MIN_DWELL_MS) {
      trackFeatureTimeSpent(state.feature, durationMs);
    }
    state.since = null;
  };

  useEffect(() => {
    applyRouteReplayBoosts(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding flushDwell would change when this runs
  useEffect(() => {
    const feature = featureFromPath(location.pathname);
    if (dwell.current?.feature === feature) {
      return;
    }
    flushDwell();
    trackFeatureViewed(feature, location.pathname);
    dwell.current = { feature, since: Date.now() };
  }, [location.pathname]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding flushDwell would change when this runs
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushDwell();
      } else if (dwell.current) {
        dwell.current.since = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flushDwell);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flushDwell);
    };
  }, []);
}
